// DR-017 tenant staff factor-reset — searchable, tenant-scoped staff PICKER (replaces the raw Staff-ID
// text field). Admin/IT select a staff member BY NAME; the raw internal ref is used only programmatically
// for the reset call and is never shown. Reset is DR-017 step-up protected, revokes the target's factor/
// assurance/recovery, and returns NO secret — the target re-enrols privately on their next sign-in.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFreshIdToken } from '../../../lib/firebase';
import { mfaApi, MFA_STATES, isValidOtp } from '../../../lib/mfaClient';
import { tenantHeaders } from '../../../lib/tenantSession';
import { useBusiness } from '../../../contexts/BusinessContext';

const base = () => process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';
async function api(path, method = 'GET', body) {
  const token = await getFreshIdToken();
  const res = await fetch(`${base()}/api/v1${path}`, {
    method,
    // Tenant-governed MFA administration with no path tenant — the active tenant must travel
    // on the header so the roster and any reset act on the tenant the admin is actually in.
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = null; try { data = await res.json(); } catch { data = {}; }
  return { ok: res.ok, status: res.status, data, state: data?.detail?.state };
}

const STATE_LABEL = {
  ENROLLED: ['Enrolled', 'text-green-700 bg-green-50'],
  NOT_ENROLLED: ['Not enrolled', 'text-gray-600 bg-gray-100'],
  RESET_REQUIRED: ['Reset — must re-enrol', 'text-amber-700 bg-amber-50'],
  LOCKED: ['Locked', 'text-red-700 bg-red-50'],
  RECOVERY_REQUIRED: ['Recovery required', 'text-amber-700 bg-amber-50'],
};

export default function MfaStaffResetPicker() {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState('');
  const { currentBusiness } = useBusiness();
  const tenant = currentBusiness?.brandId || '';
  const tenantRef = useRef(tenant);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);     // { ref, display_name, role, mfa_state }
  const [busy, setBusy] = useState(false);
  const [stepUp, setStepUp] = useState(false);         // reset needs a step-up code
  const [code, setCode] = useState('');
  const [done, setDone] = useState('');

  // Same tenant-identity rule as the policy panel: this roster belongs to ONE tenant, so it is
  // keyed and invalidated by the active tenant and a late reply from the previous one is dropped.
  const load = useCallback(async (forTenant) => {
    if (!forTenant) return;
    setError('');
    const r = await api('/mfa/staff');
    if (tenantRef.current !== forTenant) return;   // superseded by a tenant switch
    if (!r.ok) { setError(r.status === 403 ? 'Not authorized to view the staff roster.' : 'Could not load staff.'); setRows([]); return; }
    setRows(r.data.staff || []);
  }, []);
  useEffect(() => {
    tenantRef.current = tenant;
    setError(''); setRows([]);
    load(tenant);
  }, [tenant, load]);

  const filtered = (rows || []).filter((s) =>
    !query.trim() || String(s.display_name).toLowerCase().includes(query.trim().toLowerCase()));

  // A reset only makes sense for a staff member who HAS a factor / a resettable MFA state. A not-enrolled
  // member is driven by the tenant policy at their next sign-in — resetting them is a no-op and is blocked.
  const resettable = (s) => ['ENROLLED', 'RESET_REQUIRED', 'LOCKED', 'RECOVERY_REQUIRED'].includes(s.mfa_state);
  const selectStaff = (s) => {
    setStepUp(false); setCode(''); setDone(''); setError('');
    if (!resettable(s)) {
      setSelected(null);
      setDone(`${s.display_name} is not enrolled — they will be prompted according to the tenant MFA policy at their next sign-in. Nothing to reset.`);
      return;
    }
    setSelected(s);
  };

  // Perform the reset; on a step-up challenge, verify one code then retry exactly once.
  const doReset = async (verifyCode) => {
    setBusy(true); setError('');
    if (verifyCode) {
      const v = await mfaApi.verify(verifyCode.trim(), 'step_up', getFreshIdToken);
      if (!v.ok) {
        setBusy(false); setCode('');
        // 409 = the code was already used / replayed, or the actor has no factor to step up with.
        if (v.status === 409) setError('That code can’t be used. If you don’t have your own authenticator set up, set it up first (Settings → Security); otherwise wait for a fresh 6-digit code and try again.');
        else setError('Incorrect verification code.');
        return;
      }
    }
    const r = await api(`/mfa/staff/${encodeURIComponent(selected.ref)}/reset`, 'POST');
    if (!r.ok && r.status === 403 && r.state === MFA_STATES.STEP_UP_REQUIRED && !verifyCode) {
      setBusy(false); setStepUp(true); return;                 // prompt for a step-up code
    }
    setBusy(false);
    if (!r.ok) {
      if (r.status === 404) setError('Staff member not found in your organization.');
      else if (r.status === 409) setError('This staff member has no authenticator to reset.');
      else if (r.status === 403) setError('You are not authorized to reset this staff member.');
      else setError('Reset failed.');
      return;
    }
    setDone(`${selected.display_name} must set up a new authenticator on their next sign-in.`);
    setSelected(null); setStepUp(false); setCode('');
    load();
  };

  return (
    <div className="border-t pt-3">
      <div className="text-sm font-medium mb-1">Reset a staff member’s authenticator</div>
      <p className="text-xs text-gray-500 mb-2">
        Search by name and select a person. Resetting revokes their authenticator and forces a fresh
        private enrolment — you never see their new setup key or recovery codes.
      </p>

      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search staff by name…"
        aria-label="Search staff by name"
        className="w-full border rounded-md px-3 py-2 text-sm mb-2" />

      {rows === null ? (
        <p className="text-sm text-gray-500">Loading staff…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500">{query ? 'No matching staff.' : 'No staff found.'}</p>
      ) : (
        <ul className="max-h-56 overflow-y-auto border rounded-md divide-y">
          {filtered.map((s) => {
            const [label, cls] = STATE_LABEL[s.mfa_state] || STATE_LABEL.NOT_ENROLLED;
            return (
              <li key={s.ref}>
                <button type="button" onClick={() => selectStaff(s)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50">
                  <span className="min-w-0">
                    <span className="font-medium text-gray-900">{s.display_name}</span>
                    <span className="text-gray-500 capitalize"> · {s.role === 'it' ? 'IT/Integration' : s.role}</span>
                    {!s.active && <span className="text-red-600"> · inactive</span>}
                  </span>
                  <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${cls}`}>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {done && <p className="mt-2 text-sm text-green-700" role="status">{done}</p>}
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}

      {/* Confirmation — no raw id shown; step-up protected; single mutation. */}
      {selected && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-sm font-medium">Reset authenticator for {selected.display_name}?</div>
          <p className="text-xs text-gray-700 mt-1">
            This revokes {selected.display_name}’s current authenticator, MFA assurance and recovery codes.
            They must enrol a new authenticator during their next sign-in. You will not see their new setup
            key or recovery codes.
          </p>
          {stepUp && (
            <div className="mt-2">
              <label className="text-xs font-medium">Enter your own 6-digit authenticator code to confirm</label>
              <input inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full mt-1 px-3 py-2 border rounded-md tracking-widest text-center font-mono" placeholder="123456" />
            </div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSelected(null); setStepUp(false); setCode(''); }} disabled={busy}
              className="text-sm px-3 py-1.5 rounded-md border">Cancel</button>
            <button onClick={() => doReset(stepUp ? code : undefined)}
              disabled={busy || (stepUp && !isValidOtp(code))}
              className="text-sm px-3 py-1.5 rounded-md bg-red-600 text-white disabled:opacity-50">
              {busy ? 'Resetting…' : 'Reset authenticator'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
